import { useState } from "react";
import { ButtonAction } from "./TypeCard";
import { isProcessingCheckout } from "../../payments/customer/PaymentStatus";
import { useContract, useContractContext } from "../ContractContext";
import { useContractClient } from "../../api/ContractClient";
import { captureException } from "@sentry/browser";
import { useHistory, useLocation } from "react-router";
import {
  ContractUpgradeProduct,
  PaymentViewModel,
} from "../../payments/PaymentViewModel";
import { ContractType, ContractViewModel } from "../model/ContractViewModel";
import { ReactNode } from "react";
import { getRelativeUrlPart } from "../../utils";
import { ContactInfo } from "../../../../common/lib/model/contract/ContractingParty";
import { useContractUrls } from "../routing/ContractUrls";
import { getContractTypeKey } from "../create-contract/ContractTypeName";
import { addQueryParam, addRouteParams } from "../../shared/utils/Location";
import { ApiError } from "../../api/ApiClient";
import { ContractValidationResult } from "../validation/ContractValidation";
import { GetContractUpgradeProductsResponse } from "./ContractTypePage";

export interface UpgradeContractButtonProps {
  product: ContractUpgradeProduct;
  variant: "primary" | "secondary" | "outline" | "dark";
  children: ReactNode;
}

interface CreateContractUpgradePaymentResponse {
  contract: ContractViewModel;
  payment?: PaymentViewModel;
  url?: string;
}

export function useContractTypeUpgradeAction(
  upgrade: GetContractUpgradeProductsResponse,
  contractType: ContractType,
  returnPath?: string
) {
  const match = upgrade.upgrades.find(
    (x) => x.product.contractType === contractType
  );

  if (!match) {
    throw new Error(`No ${getContractTypeKey(contractType)} product found!`);
  }

  const action = useContractUpgradeAction(
    { ...match, returnPath },
    upgrade.payment?.payment || null
  );

  return {
    ...action,
    ...match,
  };
}

export function useContractUpgradeAction(
  {
    product,
    validation,
    updateContactInfo,
    returnPath: overrideReturnPath,
  }: {
    product: ContractUpgradeProduct;
    validation?: ContractValidationResult;
    updateContactInfo?: ContactInfo;
    returnPath?: string;
  },
  payment: PaymentViewModel | null
) {
  const location = useLocation();
  const returnPath = overrideReturnPath || location.pathname;
  const contract = useContract();
  const history = useHistory();
  const client = useContractClient();
  const { onChange } = useContractContext();

  const { price, taxAmount, contractType } = product;

  const [isCreatingPayment, setIsCreatingPayment] = useState(false);
  const [createPaymentError, setCreatePaymentError] = useState<ApiError | null>(
    null
  );
  const [waitingForRedirect, setWaitingForRedirect] = useState(false);

  async function onClick() {
    setIsCreatingPayment(true);
    setCreatePaymentError(null);
    try {
      const response = await client.put<CreateContractUpgradePaymentResponse>(
        `contract/${contract.id}/contractType/payment`,
        {
          payment: {
            amount: price,
            taxAmount,
          },
          contractType,
          returnPath,
          updateContactInfo,
        }
      );
      setWaitingForRedirect(true);
      setIsCreatingPayment(false);
      const { url, payment, contract: updated } = response;
      onChange(updated);
      if (url) history.push(getRelativeUrlPart(new URL(url)));
      const paymentUrl = payment?.paymentDetails?.paymentUrl;
      if (paymentUrl) window.location.href = paymentUrl;
    } catch (error) {
      setCreatePaymentError(error as ApiError);
      captureException(error);
      setIsCreatingPayment(false);
    }
  }

  const checkoutIsLoading = payment && isProcessingCheckout(payment);

  const paymentUrl =
    payment &&
    payment.contractUpgradePayment?.product.contractType === contractType &&
    checkoutIsLoading &&
    payment.paymentDetails.paymentUrl;

  const hasRequiredInfo = validation ? validation.isValid : true;

  const urls = useContractUrls();

  const fillRequiredInfoUrl = addQueryParam(
    addRouteParams(urls.typePaymentRequirement, {
      contractType: getContractTypeKey(product.contractType),
    }),
    new URLSearchParams({ returnPath })
  );

  const action: ButtonAction =
    !waitingForRedirect && paymentUrl
      ? { href: paymentUrl }
      : !hasRequiredInfo
      ? {
          linkTo: fillRequiredInfoUrl,
        }
      : {
          isLoading: isCreatingPayment || waitingForRedirect,
          onClick,
        };

  return {
    hasRequiredInfo,
    action,
    error: createPaymentError,
  };
}
